import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Play, Pause, Volume2, VolumeX, Maximize, Minimize, Settings, Subtitles, Languages, ChevronRight, Check } from "lucide-react";
import Hls from "hls.js";
import api from "@/services/api";
import { addToHistory, saveProgress, getProgress } from "@/utils/watchData";

// Stream URL-i birbaşa HTTPS-ə çevir (redirect artıq HTTPS→HTTPS)
const resolveStreamUrl = (url) => {
  if (!url) return null;
  // Ensure HTTPS
  let resolved = url.replace('http://', 'https://');
  // VOD links from one.fiber.net.az redirect to arc1.fiber.net.az - browser follows automatically
  return resolved;
};

export default function PlayerPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = location.state?.returnPath || '/app';
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const controlTimerRef = useRef(null);
  const mouseInsideRef = useRef(false);
  
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Audio & Subtitle track states
  const [audioTracks, setAudioTracks] = useState([]);
  const [textTracks, setTextTracks] = useState([]);
  const [activeAudioIdx, setActiveAudioIdx] = useState(0);
  const [activeSubIdx, setActiveSubIdx] = useState(-1); // -1 = off
  const [settingsOpen, setSettingsOpen] = useState(false); // main settings menu
  const [settingsPanel, setSettingsPanel] = useState(null); // 'audio' | 'subtitle' | null

  useEffect(() => {
    const fetchContent = async () => {
      try {
        let data;
        let streamUrl;

        if (type === "live") {
          // For live content, get the channel info
          const response = await api.getLiveContent();
          const channel = response.content?.find(c => c.stream_id === id);
          if (channel) {
            data = channel;
            // Use m3u8 for HLS playback (better compatibility)
            streamUrl = channel.links?.m3u8 || channel.links?.ts;
            console.log("Available links:", channel.links);
          }
        } else if (type === "vod") {
          // For VOD, get movie details
          const response = await api.getVodDetails(id);
          data = { ...response.vod, info: response.info };
          streamUrl = response.vod?.links?.m3u8 || response.vod?.links?.ts || response.vod?.links?.mp4 || response.vod?.links?.mkv;
        } else if (type === "series") {
          // For series, get series details
          const response = await api.getSeriesDetails(id);
          data = response;
          // For series, select first episode
          const seasons = response.episodes ? Object.keys(response.episodes) : [];
          if (seasons.length > 0) {
            const firstEps = response.episodes[seasons[0]];
            if (firstEps?.[0]) {
              const firstEp = firstEps[0];
              streamUrl = firstEp.links?.m3u8 || firstEp.links?.ts || firstEp.links?.mp4 || firstEp.links?.mkv;
            }
          }
        }

        if (!data) {
          throw new Error("Məzmun tapılmadı");
        }

        const directUrl = resolveStreamUrl(streamUrl);
        console.log("Stream URL:", directUrl);
        setContent({ ...data, streamUrl: directUrl });

        // Track in watch history
        if (type !== "live") {
          addToHistory({
            id,
            name: data.name || data.info?.name,
            poster: data.info?.movie_image || data.stream_icon || data.info?.cover || data.cover,
            type,
            genre: data.info?.genre || data.genre,
            rating: data.info?.rating || data.rating,
            year: (data.info?.release_date || data.release_date || "").slice(0, 4),
          });
        }
      } catch (err) {
        console.error("Failed to fetch content:", err);
        setError(err.message || "Məzmun yüklənə bilmədi");
      } finally {
        setLoading(false);
      }
    };

    fetchContent();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [type, id]);

  useEffect(() => {
    if (!content?.streamUrl || !videoRef.current) return;

    const video = videoRef.current;
    const streamUrl = content.streamUrl;
    video.volume = volume;

    console.log("Initializing player with URL:", streamUrl);

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // HLS.js for m3u8 streams
    if (streamUrl.includes(".m3u8")) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: type === "live",
          liveDurationInfinity: true,
          liveBackBufferLength: 30,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          // Force specific audio codec string
          // This overrides the auto-detected codec
          abrEwmaDefaultEstimate: 500000,
          // Try to use more compatible settings
          stretchShortVideoTrack: true,
          maxAudioFramesDrift: 1,
        });

        hls.loadSource(streamUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log("HLS manifest parsed");
          console.log("HLS audio tracks:", hls.audioTracks);
          console.log("HLS subtitle tracks:", hls.subtitleTracks);

          // Detect HLS audio tracks
          if (hls.audioTracks && hls.audioTracks.length > 0) {
            const tracks = hls.audioTracks.map((t, i) => ({
              index: i,
              id: t.id,
              label: t.name || `Audio ${i + 1}`,
              language: t.lang || '',
            }));
            setAudioTracks(tracks);
            setActiveAudioIdx(hls.audioTrack);
          }

          // Detect HLS subtitle tracks
          if (hls.subtitleTracks && hls.subtitleTracks.length > 0) {
            const tracks = hls.subtitleTracks.map((t, i) => ({
              index: i,
              id: t.id,
              label: t.name || t.lang || `Subtitle ${i + 1}`,
              language: t.lang || '',
            }));
            setTextTracks(tracks);
          }

          setLoading(false);
          video.play().catch(() => {});
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error("HLS Error:", data.type, data.details, data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log("Network error, attempting recovery...");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log("Media error, attempting recovery...");
                hls.recoverMediaError();
                break;
              default:
                setError("Video yüklənə bilmədi");
                break;
            }
          }
        });

        hlsRef.current = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native HLS support
        video.src = streamUrl;
        video.addEventListener("loadedmetadata", () => {
          setLoading(false);
          video.play().catch(console.error);
        });
      } else {
        setError("HLS dəstəklənmir");
      }
    } else {
      // Direct video playback (mp4, mkv, etc.)
      video.src = streamUrl;
      video.addEventListener("loadedmetadata", () => {
        setLoading(false);
        video.play().catch(() => {}); // Ignore autoplay errors
      });
      video.addEventListener("error", () => {
        setError("Video yüklənə bilmədi. Format dəstəklənmir.");
      });
    }

    // Cleanup
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      // Prevent "play() was interrupted" errors on unmount
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [content, type]);

  // Auto-hide controls + cursor (same approach as LiveTVPage)
  const handlePlayerMouseMove = useCallback(() => {
    mouseInsideRef.current = true;
    setShowControls(true);
    const el = containerRef.current;
    if (el) el.style.cursor = 'default';
    clearTimeout(controlTimerRef.current);
    controlTimerRef.current = setTimeout(() => {
      if (mouseInsideRef.current) {
        setShowControls(false);
        if (containerRef.current) containerRef.current.style.cursor = 'none';
      }
    }, 3000);
  }, []);

  const handlePlayerMouseLeave = useCallback(() => {
    mouseInsideRef.current = false;
    clearTimeout(controlTimerRef.current);
    setShowControls(false);
    if (containerRef.current) containerRef.current.style.cursor = 'default';
  }, []);

  const handlePlayerMouseEnter = useCallback(() => {
    mouseInsideRef.current = true;
    setShowControls(true);
    if (containerRef.current) containerRef.current.style.cursor = 'default';
    clearTimeout(controlTimerRef.current);
    controlTimerRef.current = setTimeout(() => {
      setShowControls(false);
      if (containerRef.current) containerRef.current.style.cursor = 'none';
    }, 3000);
  }, []);

  // Fullscreen change listener
  useEffect(() => {
    const handleFSChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFSChange);
    return () => document.removeEventListener("fullscreenchange", handleFSChange);
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, []);

  const changeVolume = useCallback((val) => {
    const video = videoRef.current;
    if (!video) return;
    const v = Math.max(0, Math.min(1, val));
    video.volume = v;
    setVolume(v);
    if (v === 0) { video.muted = true; setMuted(true); }
    else if (video.muted) { video.muted = false; setMuted(false); }
  }, []);

  const toggleFullscreen = async () => {
    const container = containerRef.current;
    if (!container) return;

    if (document.fullscreenElement) {
      try { if (screen.orientation?.unlock) screen.orientation.unlock(); } catch { /* ignore */ }
      await document.exitFullscreen();
    } else {
      await container.requestFullscreen();
      try { if (screen.orientation?.lock) await screen.orientation.lock('landscape'); } catch { /* ignore — desktop/iOS */ }
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video) {
      setCurrentTime(video.currentTime || 0);
      setDuration(video.duration || 0);
    }
  };

  // Save progress periodically
  useEffect(() => {
    if (type === "live" || !content) return;
    const interval = setInterval(() => {
      const video = videoRef.current;
      if (video && video.duration > 0 && !video.paused) {
        saveProgress(id, video.currentTime, video.duration, {
          name: content.name || content.info?.name,
          poster: content.info?.movie_image || content.stream_icon || content.info?.cover || content.cover,
          type,
          genre: content.info?.genre || content.genre,
        });
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [content, id, type]);

  // Resume from saved position
  useEffect(() => {
    if (type === "live" || !content?.streamUrl) return;
    const saved = getProgress(id);
    if (saved && saved.currentTime > 10) {
      const video = videoRef.current;
      if (video) {
        const tryResume = () => {
          if (video.duration > 0) {
            video.currentTime = saved.currentTime;
          }
        };
        video.addEventListener('loadedmetadata', tryResume, { once: true });
      }
    }
  }, [content, id, type]);

  // Detect audio and text tracks from video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !content?.streamUrl) return;

    const detectTracks = () => {
      // Detect audio tracks
      if (video.audioTracks && video.audioTracks.length > 0) {
        const tracks = [];
        for (let i = 0; i < video.audioTracks.length; i++) {
          const t = video.audioTracks[i];
          tracks.push({
            index: i,
            id: t.id,
            label: t.label || `Audio ${i + 1}`,
            language: t.language || '',
            enabled: t.enabled,
          });
          if (t.enabled) setActiveAudioIdx(i);
        }
        if (tracks.length > 0) setAudioTracks(tracks);
      }

      // Detect text tracks (embedded subtitles)
      if (video.textTracks && video.textTracks.length > 0) {
        const tracks = [];
        for (let i = 0; i < video.textTracks.length; i++) {
          const t = video.textTracks[i];
          // Skip metadata tracks (used internally by some browsers)
          if (t.kind === 'metadata') continue;
          tracks.push({
            index: i,
            label: t.label || t.language || `Subtitle ${i + 1}`,
            language: t.language || '',
            kind: t.kind,
            mode: t.mode,
          });
        }
        if (tracks.length > 0) setTextTracks(tracks);
      }
    };

    // Try on loadedmetadata
    video.addEventListener('loadedmetadata', detectTracks);
    video.addEventListener('canplay', detectTracks);
    video.addEventListener('playing', detectTracks);
    // Also try after delays (some browsers populate tracks late)
    const timer1 = setTimeout(detectTracks, 2000);
    const timer2 = setTimeout(detectTracks, 5000);

    // Listen for track additions
    if (video.textTracks) {
      video.textTracks.addEventListener('addtrack', detectTracks);
    }
    if (video.audioTracks) {
      video.audioTracks.addEventListener('addtrack', detectTracks);
    }

    return () => {
      video.removeEventListener('loadedmetadata', detectTracks);
      video.removeEventListener('canplay', detectTracks);
      video.removeEventListener('playing', detectTracks);
      clearTimeout(timer1);
      clearTimeout(timer2);
      if (video.textTracks) {
        video.textTracks.removeEventListener('addtrack', detectTracks);
      }
      if (video.audioTracks) {
        video.audioTracks.removeEventListener('addtrack', detectTracks);
      }
    };
  }, [content]);

  // Switch audio track (HLS.js or native)
  const switchAudioTrack = useCallback((idx) => {
    // HLS.js audio track switching
    if (hlsRef.current && hlsRef.current.audioTracks?.length > 0) {
      hlsRef.current.audioTrack = idx;
      setActiveAudioIdx(idx);
      return;
    }
    // Native fallback (Safari)
    const video = videoRef.current;
    if (video?.audioTracks) {
      for (let i = 0; i < video.audioTracks.length; i++) {
        video.audioTracks[i].enabled = (i === idx);
      }
      setActiveAudioIdx(idx);
    }
  }, []);

  // Switch subtitle track (HLS.js or native)
  const switchSubtitleTrack = useCallback((idx) => {
    // HLS.js subtitle track switching
    if (hlsRef.current && hlsRef.current.subtitleTracks?.length > 0) {
      hlsRef.current.subtitleTrack = idx;
      hlsRef.current.subtitleDisplay = true;
      setActiveSubIdx(idx);
      return;
    }
    // Native fallback
    const video = videoRef.current;
    if (video?.textTracks) {
      for (let i = 0; i < video.textTracks.length; i++) {
        video.textTracks[i].mode = (i === idx) ? 'showing' : 'disabled';
      }
      setActiveSubIdx(idx);
    }
  }, []);

  // Turn off all subtitles
  const turnOffSubtitles = useCallback(() => {
    // HLS.js
    if (hlsRef.current && hlsRef.current.subtitleTracks?.length > 0) {
      hlsRef.current.subtitleTrack = -1;
      setActiveSubIdx(-1);
      return;
    }
    // Native fallback
    const video = videoRef.current;
    if (video?.textTracks) {
      for (let i = 0; i < video.textTracks.length; i++) {
        video.textTracks[i].mode = 'disabled';
      }
      setActiveSubIdx(-1);
    }
  }, []);

  // Close settings when controls hide
  useEffect(() => {
    if (!showControls) {
      setSettingsOpen(false);
      setSettingsPanel(null);
    }
  }, [showControls]);

  // Language code to display name
  const langName = (code) => {
    const map = { az: 'Azərbaycan', ru: 'Русский', en: 'English', tr: 'Türkçe', und: 'Naməlum' };
    return map[code?.toLowerCase()] || code || '';
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * duration;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading && !content) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center">
        <div className="text-white">Yüklənir...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error}</div>
        <button
          onClick={() => navigate(returnPath)}
          className="px-4 py-2 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
        >
          Geri qayıt
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      onMouseMove={handlePlayerMouseMove}
      onMouseLeave={handlePlayerMouseLeave}
      onMouseEnter={handlePlayerMouseEnter}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        onClick={togglePlay}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => navigate(returnPath)}
        playsInline
        muted={muted}
      />

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-white">Yüklənir...</div>
        </div>
      )}

      {/* Controls Overlay */}
      <div
        onClick={(e) => {
          // Toggle play/pause when clicking on empty area (not on buttons/inputs)
          if (e.target.closest('button') || e.target.closest('input') || e.target.closest('[data-controls]')) return;
          togglePlay();
        }}
        className={`absolute inset-0 transition-opacity duration-300 ${
          showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Top gradient */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/80 to-transparent" />
        
        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/80 to-transparent" />

        {/* Top controls */}
        <div className="absolute top-0 left-0 right-0 p-3 sm:p-6 flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate(returnPath)}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-semibold truncate">{content?.name}</h1>
            {type === "live" && (
              <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-red-400">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                CANLI
              </span>
            )}
          </div>
        </div>

        {/* Center play button */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {!playing && (
            <button
              onClick={togglePlay}
              className="p-4 sm:p-6 rounded-full bg-white/20 backdrop-blur-sm pointer-events-auto hover:bg-white/30 transition-colors"
            >
              <Play className="w-10 h-10 sm:w-12 sm:h-12" fill="white" />
            </button>
          )}
        </div>

        {/* Bottom controls */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6">
          {/* Progress bar (only for VOD) */}
          {type !== "live" && duration > 0 && (
            <div
              data-controls
              className="h-1 bg-white/30 rounded-full mb-3 sm:mb-4 cursor-pointer group"
              onClick={handleSeek}
            >
              <div
                className="h-full bg-blue-500 rounded-full relative group-hover:h-1.5 transition-all"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 sm:gap-4 min-w-0">
              <button
                onClick={togglePlay}
                className="p-2 sm:p-2 rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
              >
                {playing ? (
                  <Pause className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                  <Play className="w-5 h-5 sm:w-6 sm:h-6" fill="white" />
                )}
              </button>

              <div className="hidden sm:flex items-center gap-1 group/vol">
                <button
                  onClick={toggleMute}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
                >
                  {muted ? (
                    <VolumeX className="w-5 h-5 sm:w-6 sm:h-6" />
                  ) : (
                    <Volume2 className="w-5 h-5 sm:w-6 sm:h-6" />
                  )}
                </button>
                <input
                  data-testid="volume-slider"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={muted ? 0 : volume}
                  onChange={(e) => changeVolume(parseFloat(e.target.value))}
                  className="hidden sm:block w-20 h-1 accent-blue-500 cursor-pointer"
                />
              </div>

              {type !== "live" && (
                <span className="text-xs sm:text-sm text-zinc-400 truncate">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
              {/* Subtitle button (quick toggle) */}
              {textTracks.length > 0 && (
                <button
                  data-testid="subtitle-toggle-btn"
                  onClick={() => {
                    if (activeSubIdx >= 0) turnOffSubtitles();
                    else switchSubtitleTrack(0);
                  }}
                  className={`p-2 rounded-full hover:bg-white/20 transition-colors ${activeSubIdx >= 0 ? 'text-blue-400' : ''}`}
                  title="Altyazı"
                >
                  <Subtitles className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}

              {/* Settings button - always show for VOD */}
              {type !== "live" && (
                <div className="relative">
                  <button
                    data-testid="settings-btn"
                    onClick={() => {
                      setSettingsOpen(prev => !prev);
                      setSettingsPanel(null);
                    }}
                    className="p-2 rounded-full hover:bg-white/20 transition-colors"
                  >
                    <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>

                  {/* Settings dropdown */}
                  {settingsOpen && (
                    <div
                      data-testid="settings-menu"
                      className="absolute bottom-12 right-0 w-72 sm:w-64 max-w-[calc(100vw-1.5rem)] bg-zinc-900/95 backdrop-blur-md border border-zinc-700/50 rounded-xl overflow-hidden shadow-2xl"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!settingsPanel && (
                        <div className="py-1">
                          {/* Audio option */}
                          <button
                            data-testid="audio-track-menu-btn"
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/10 transition-colors"
                            onClick={() => audioTracks.length > 0 && setSettingsPanel('audio')}
                          >
                            <div className="flex items-center gap-3">
                              <Languages className="w-5 h-5 text-zinc-400" />
                              <span>Audio</span>
                            </div>
                            <div className="flex items-center gap-2 text-zinc-400 text-sm">
                              <span>{audioTracks.length > 0 ? (audioTracks[activeAudioIdx]?.label || `Track ${activeAudioIdx + 1}`) : 'Yoxdur'}</span>
                              {audioTracks.length > 0 && <ChevronRight className="w-4 h-4" />}
                            </div>
                          </button>
                          {/* Subtitle option */}
                          <button
                            data-testid="subtitle-menu-btn"
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/10 transition-colors"
                            onClick={() => textTracks.length > 0 && setSettingsPanel('subtitle')}
                          >
                            <div className="flex items-center gap-3">
                              <Subtitles className="w-5 h-5 text-zinc-400" />
                              <span>Altyazı</span>
                            </div>
                            <div className="flex items-center gap-2 text-zinc-400 text-sm">
                              <span>{textTracks.length > 0 ? (activeSubIdx >= 0 ? (textTracks[activeSubIdx]?.label || `Sub ${activeSubIdx + 1}`) : 'Bağlı') : 'Yoxdur'}</span>
                              {textTracks.length > 0 && <ChevronRight className="w-4 h-4" />}
                            </div>
                          </button>
                        </div>
                      )}

                      {/* Audio track sub-panel */}
                      {settingsPanel === 'audio' && (
                        <div>
                          <button
                            className="w-full px-4 py-2.5 flex items-center gap-2 text-sm text-zinc-400 hover:bg-white/10 border-b border-zinc-700/50"
                            onClick={() => setSettingsPanel(null)}
                          >
                            <ArrowLeft className="w-4 h-4" /> Audio
                          </button>
                          {audioTracks.map((t, i) => (
                            <button
                              key={i}
                              data-testid={`audio-track-${i}`}
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/10 transition-colors"
                              onClick={() => switchAudioTrack(i)}
                            >
                              <span className={activeAudioIdx === i ? 'text-blue-400' : ''}>
                                {t.label}{t.language ? ` (${langName(t.language)})` : ''}
                              </span>
                              {activeAudioIdx === i && <Check className="w-4 h-4 text-blue-400" />}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Subtitle sub-panel */}
                      {settingsPanel === 'subtitle' && (
                        <div>
                          <button
                            className="w-full px-4 py-2.5 flex items-center gap-2 text-sm text-zinc-400 hover:bg-white/10 border-b border-zinc-700/50"
                            onClick={() => setSettingsPanel(null)}
                          >
                            <ArrowLeft className="w-4 h-4" /> Altyazı
                          </button>
                          <button
                            data-testid="subtitle-off"
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/10 transition-colors"
                            onClick={turnOffSubtitles}
                          >
                            <span className={activeSubIdx === -1 ? 'text-blue-400' : ''}>Bağlı</span>
                            {activeSubIdx === -1 && <Check className="w-4 h-4 text-blue-400" />}
                          </button>
                          {textTracks.map((t, i) => (
                            <button
                              key={i}
                              data-testid={`subtitle-track-${i}`}
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/10 transition-colors"
                              onClick={() => switchSubtitleTrack(i)}
                            >
                              <span className={activeSubIdx === i ? 'text-blue-400' : ''}>
                                {t.label}{t.language ? ` (${langName(t.language)})` : ''}
                              </span>
                              {activeSubIdx === i && <Check className="w-4 h-4 text-blue-400" />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <button
                data-testid="fullscreen-btn"
                onClick={toggleFullscreen}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
                title={isFullscreen ? "Kiçilt" : "Tam ekran"}
              >
                {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
